import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
  useMemo,
  forwardRef,
} from 'react';
import cx from 'clsx';
import Popover from 'antd/es/popover';

import { IconRcImgFail, IconRcLoading } from 'assets';

import './index.css';

interface LazyMediaProps {
  src?: string;
  alt?: string;
  type?: string;
  className?: string;
  failedClassName?: string;
  failedPlaceholder?: ReactNode;
  loadingPlaceholder?: ReactNode;
  hoverShow?: boolean;
  pixelated?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  style?: React.CSSProperties;
  hoverChildren?: ReactNode;
  disableLazy?: boolean;
}

enum LOAD_STATE {
  LOADING,
  SUCCESS,
  FAIL,
}

export enum MEDIA_TYPE {
  IMAGE = 'image',
  IMAGE_URL = 'image_url',
  VIDEO_URL = 'video_url',
  AUDIO_URL = 'audio_url',
}

const Media = forwardRef(
  (
    {
      type = MEDIA_TYPE.IMAGE_URL,
      src,
      alt,
      handleSuccess,
      handleError,
      pixelated,
      style,
    }: {
      handleSuccess?(): void;
      handleError?(): void;
    } & Pick<LazyMediaProps, 'type' | 'src' | 'alt' | 'pixelated' | 'style'>,
    ref: React.Ref<HTMLImageElement | HTMLVideoElement>,
  ) => (
    <>
      {(type === MEDIA_TYPE.IMAGE || type === MEDIA_TYPE.IMAGE_URL) && (
        <img
          className={cx('db-lazyMedia-img', pixelated && 'is-pixelated')}
          src={src}
          alt={alt}
          ref={ref as React.Ref<HTMLImageElement>}
          onLoad={handleSuccess}
          onError={handleError}
          style={style}
        />
      )}
      {type === MEDIA_TYPE.VIDEO_URL && (
        <video
          className="db-lazyMedia-video"
          src={src}
          ref={ref as React.Ref<HTMLVideoElement>}
          controls
          onLoadedMetadata={handleSuccess}
          onError={handleError}
          style={style}
        />
      )}
    </>
  ),
);

export const LazyMedia = ({
  src,
  alt,
  type = 'image',
  className,
  failedPlaceholder,
  hoverShow,
  loadingPlaceholder,
  style,
  hoverChildren,
  disableLazy = true,
  failedClassName,
  ...rest
}: LazyMediaProps & IntersectionObserverInit) => {
  const mediaRef = useRef<HTMLImageElement | HTMLVideoElement>(null);
  const [loadState, setLoadState] = useState(LOAD_STATE.LOADING);
  const ele = useRef<HTMLDivElement>(null);
  const [inVisible, setInVisible] = useState(disableLazy);
  const [popoverVisble, setPopoverVisible] = useState(false);

  const observerCallback = useRef<IntersectionObserverCallback>(entries => {
    if (!inVisible && entries[0].isIntersecting) {
      setInVisible(true);
    }
  });

  useEffect(() => {
    if (!src) {
      setLoadState(LOAD_STATE.FAIL);
    } else if ((mediaRef.current as HTMLImageElement)?.complete) {
      if ((mediaRef.current as HTMLImageElement).naturalWidth > 0) {
        setLoadState(LOAD_STATE.SUCCESS);
      } else {
        setLoadState(LOAD_STATE.FAIL);
      }
    } else if ((mediaRef.current as HTMLVideoElement)?.readyState >= 3) {
      setLoadState(LOAD_STATE.SUCCESS);
    } else {
      setLoadState(LOAD_STATE.LOADING);
    }
  }, [src, mediaRef]);

  useEffect(() => {
    if (!disableLazy) {
      const observer = new IntersectionObserver(observerCallback.current, {
        threshold: [0.01, 0.1, 0.99, 1],
      });

      if (ele.current) {
        observer.observe(ele.current);
      }

      return () => observer.disconnect();
    }
  }, [disableLazy]);

  const handleError = useCallback(() => {
    setLoadState(LOAD_STATE.FAIL);
  }, []);

  const handleSuccess = useCallback(() => {
    setLoadState(LOAD_STATE.SUCCESS);
  }, []);

  const handleVisibleChange = useCallback(
    (visible: boolean) => {
      if (visible && hoverShow && loadState !== LOAD_STATE.LOADING) {
        setPopoverVisible(true);
      } else {
        setPopoverVisible(false);
      }
    },
    [hoverShow, loadState],
  );

  const FailedPlaceholder = useMemo(
    () => failedPlaceholder || <IconRcImgFail className="db-lazyMedia-failedIcon" />,
    [failedPlaceholder],
  );

  return (
    <Popover
      content={
        <div className="db-lazyMedia-popShow">
          {loadState === LOAD_STATE.FAIL ? (
            <div className="db-lazyMedia-popFailed">{FailedPlaceholder}</div>
          ) : (
            <Media src={src} alt={alt} type={type} />
          )}
          {hoverChildren}
        </div>
      }
      onVisibleChange={handleVisibleChange}
      visible={popoverVisble}
      placement="top">
      <div
        className={cx('db-lazyMedia', className, loadState === LOAD_STATE.FAIL && failedClassName)}
        ref={ele}
        {...rest}
        style={style}>
        {loadState === LOAD_STATE.FAIL ? (
          FailedPlaceholder
        ) : loadState === LOAD_STATE.LOADING ? (
          <div className={cx('db-lazyMedia-mask', 'is-loading')}>
            {loadingPlaceholder || <IconRcLoading className="db-lazyMedia-loadIcon" />}
          </div>
        ) : null}
        {loadState !== LOAD_STATE.FAIL && inVisible && (
          <Media
            src={src}
            alt={alt}
            ref={mediaRef}
            type={type}
            handleSuccess={handleSuccess}
            handleError={handleError}
          />
        )}
      </div>
    </Popover>
  );
};

export const LazyMediaSized = (
  props: { maxHeight: number; maxWidth: number; height: number; width: number } & LazyMediaProps &
    IntersectionObserverInit,
) => {
  const { maxHeight, maxWidth, height, width, ...rest } = props;

  const sizeStyle = useMemo(() => {
    const widthPercent = maxWidth / width;
    const heightPercent = maxHeight / height;
    const smallestPercent = Math.min(widthPercent, heightPercent);
    const _width = smallestPercent > 1 ? width : Math.ceil(width * smallestPercent);
    const _height = smallestPercent > 1 ? height : Math.ceil(height * smallestPercent);

    return {
      width: _width,
      height: _height,
    };
  }, [maxHeight, maxWidth, height, width]);
  return <LazyMedia {...rest} style={sizeStyle} />;
};
